// M10.4 — CTA placement component. Evaluates the shared rule engine
// against the current context and renders a Sked trial CTA, or nothing
// when no rule fires. onClick POSTs to the client telemetry endpoint so
// the trial_cta_clicked lead-event is captured server-side.

import { buildCtaHref, selectCta, type CtaContext, type CtaCopy } from "@shared/leadgen/cta-rules";
import { cn } from "@/lib/utils";

export interface CtaPlacementProps {
  context: CtaContext;
  className?: string;
}

export function CtaPlacement({ context, className }: CtaPlacementProps) {
  const cta = selectCta(context);
  if (!cta) return null;
  const href = buildCtaHref(cta.slug);

  return (
    <section
      aria-label="Sked Social trial call to action"
      // M12.9 — marketing cross-sell switches to the Sked brand pink
      // (#ED207C in the brand kit / SkedMark heart glyph) so users can
      // tell "this is Sked trying to sell you something" apart from the
      // in-product blurple surfaces (DigestNudge, ExecutiveSummary,
      // MetricCard). Same hue family as the SkedMark + the wordmark's
      // pink block.
      className={cn(
        "@container rounded-2xl bg-gradient-to-br from-pink-50 to-white p-5 ring-1 ring-pink-200",
        className,
      )}
    >
      {/* SKE-3473 follow-up — flipped from viewport (`sm:flex-row`) to
          container queries (`@md:flex-row`) so the card stacks whenever
          it sits inside a narrow container (e.g. the 3-column Exports
          grid ~240px per card) and only goes side-by-side when it has
          room (full-page-width usages on ReportOverview). */}
      <div className="flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
        <div className="@md:max-w-[42ch]">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-pink-700">
            Sked Social
          </p>
          <p className="mt-1 font-display text-lg font-semibold text-neutral-950">{cta.headline}</p>
          <p className="mt-1 text-sm text-neutral-700">{cta.body}</p>
        </div>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={() => void reportCtaClick(cta)}
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-pink-600 transition-colors hover:bg-pink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 @md:self-auto"
        >
          {cta.action}
          <i className="fa-regular fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

async function reportCtaClick(cta: CtaCopy): Promise<void> {
  try {
    await fetch("/api/telemetry", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event: cta.event,
        slug: cta.slug,
      }),
      keepalive: true,
    });
  } catch {
    // Telemetry failures are non-fatal — the user's outbound nav continues.
  }
}
